import { forwardRef, memo, useCallback, useMemo, type ReactNode } from 'react';
import { useLazyQuery } from '@apollo/client';
// Material Icon imports
import SearchIcon from '@mui/icons-material/Search';
// Skillmore UI Components
import useQueryCounted from '@empathco/ui-components/src/hooks/useQueryCounted';
import Lookup from '@empathco/ui-components/src/elements/Lookup';
// local imports
import { LOCATIONS_QUERY } from '../graphql/Locations';
import { Location, LocationsDocument, LocationsQueryVariables } from '../graphql/types';
import { MAX_ITEMS } from '../config/params';
// SCSS imports
import { root } from './LocationSearch.module.scss';

type LocationSearchProps = {
  readonly countryId?: number;
  readonly value?: Location | null;
  readonly onChange: (location: Location | null) => void;
  readonly disabled?: boolean | null;
  readonly fullWidth?: boolean;
  readonly error?: boolean;
  readonly helperText?: ReactNode;
}

const LocationSearch = forwardRef<HTMLDivElement, LocationSearchProps>(({
  countryId,
  value,
  onChange,
  disabled = false,
  fullWidth = false,
  error,
  helperText
}, ref) => {
  const { query: getLocations, loading, failed, results, variables } = useQueryCounted({
    data: undefined as unknown as Location,
    key: 'locations',
    lazyQuery: useLazyQuery(LOCATIONS_QUERY as typeof LocationsDocument)
  });

  const fetchParams: Partial<LocationsQueryVariables> = useMemo(() => ({
    country_id: countryId,
    limit: MAX_ITEMS
  }), [countryId]);

  const fetchLocations = useCallback(
    (params: LocationsQueryVariables) => getLocations?.({ variables: params }),
    [getLocations]);

  return (
    <Lookup
        ref={ref}
        fullWidth={fullWidth}
        type="locations"
        className={root}
        fetched={results}
        pending={loading}
        failed={failed}
        params={variables}
        fetch={fetchLocations}
        fetchParams={fetchParams}
        onChange={onChange}
        value={value}
        disabled={disabled}
        error={error}
        helperText={helperText}
        popupIcon={<SearchIcon/>}
    />
  );
});

LocationSearch.displayName = 'LocationSearch';

export default memo(LocationSearch);
